import { z } from "zod";
/**
 * Official Indian GST state / UT codes — the SINGLE source of truth for the whole
 * system (spec §4, "State code source of truth"). The Prisma seed imports this map;
 * do not maintain a second copy anywhere.
 *
 * Scope: codes 01–38 that are CURRENTLY valid, plus 97 "Other Territory" — 37
 * entries in total, matching the "seed the 37 state/UT codes" instruction in §4.
 *
 * Two codes inside the 01–38 range are deliberately ABSENT because they no longer
 * issue GSTINs:
 *   - 25 "Daman and Diu"  — merged into 26 on 26 Jan 2020
 *   - 28 "Andhra Pradesh" — pre-bifurcation code, replaced by 37
 * A GSTIN carrying 25 or 28 is therefore rejected by {@link StateCodeSchema}.
 * If a legacy document with one of those codes ever has to be accepted, add them
 * here as explicit legacy entries rather than loosening the validation.
 *
 * Code 99 ("Centre Jurisdiction") is not a supply state and is not included.
 */
export const GST_STATE_CODES = {
    "01": "Jammu and Kashmir",
    "02": "Himachal Pradesh",
    "03": "Punjab",
    "04": "Chandigarh",
    "05": "Uttarakhand",
    "06": "Haryana",
    "07": "Delhi",
    "08": "Rajasthan",
    "09": "Uttar Pradesh",
    "10": "Bihar",
    "11": "Sikkim",
    "12": "Arunachal Pradesh",
    "13": "Nagaland",
    "14": "Manipur",
    "15": "Mizoram",
    "16": "Tripura",
    "17": "Meghalaya",
    "18": "Assam",
    "19": "West Bengal",
    "20": "Jharkhand",
    "21": "Odisha",
    "22": "Chhattisgarh",
    "23": "Madhya Pradesh",
    "24": "Gujarat",
    "26": "Dadra and Nagar Haveli and Daman and Diu",
    "27": "Maharashtra",
    "29": "Karnataka",
    "30": "Goa",
    "31": "Lakshadweep",
    "32": "Kerala",
    "33": "Tamil Nadu",
    "34": "Puducherry",
    "35": "Andaman and Nicobar Islands",
    "36": "Telangana",
    "37": "Andhra Pradesh",
    "38": "Ladakh",
    "97": "Other Territory",
};
/** The state code of the business itself (spec §7.3) — asserted at seed time. */
export const MAHARASHTRA_STATE_CODE = "27";
/** Sorted `{ code, name }` list — convenient for the seed script and UI dropdowns. */
export const GST_STATE_CODE_LIST = Object.freeze(Object.keys(GST_STATE_CODES)
    .sort()
    .map((code) => Object.freeze({ code, name: GST_STATE_CODES[code] })));
/** Type guard: is this string a currently-valid GST state / UT code? */
export function isGstStateCode(value) {
    return Object.prototype.hasOwnProperty.call(GST_STATE_CODES, value);
}
/** State / UT name for a code, or `undefined` if the code is not recognised. */
export function gstStateName(code) {
    return isGstStateCode(code) ? GST_STATE_CODES[code] : undefined;
}
/**
 * A two-digit state code that must exist in {@link GST_STATE_CODES}.
 * Narrows the parsed output to the {@link StateCode} literal union.
 */
export const StateCodeSchema = z
    .string()
    .trim()
    .regex(/^[0-9]{2}$/, "State code must be exactly two digits")
    .refine((value) => isGstStateCode(value), {
    message: "Not a valid Indian GST state or union territory code",
});
//# sourceMappingURL=state-codes.js.map